import React from "react";
import Modal from "../../../Ui/Modal";
import ButtonComponent from "../../../Ui/Button";
import { formatCurrency, formatScheduledTime } from "../../../utils/utils";

// A lightweight modal to accept or deny a change request.
// Uses the shared Generic Modal for consistent look & feel.
// Props:
// - open: boolean
// - onClose: function
// - proposal: object|string|null (details of the change request)
// - onAccept: function
// - onDeny: function
const ProposalRequestAcceptDeny = ({ open, onClose, proposal, onAccept, onDeny }) => {
  console.log('render ProposalRequestAcceptDeny with proposal:', proposal);

  // Normalize proposal display
  const renderProposalDetails = () => {
    if (!proposal) {
      return (
        <p className="text-[#595959] text-[14px]">
          No additional details were provided with this change request.
        </p>
      );
    }

    if (typeof proposal === "string") {
      return <p className="text-[#595959] text-[14px] whitespace-pre-wrap">{proposal}</p>;
    }

    // If it's an object, show a simple key/value summary excluding noisy keys
    const isMediaObject = (val) =>
      val && typeof val === "object" && !Array.isArray(val) && typeof val.file_url === "string";

    const shouldCurrencyFormat = (key, val) => {
      if (val === null || val === undefined) return false;
      const k = String(key || "").toLowerCase();
      // Only format known money-like fields to avoid false positives (e.g., "2 days")
      return /(amount|estimate|price|cost)/.test(k);
    };


    // If entire proposal is a single media object, render image directly
    if (isMediaObject(proposal)) {
      return (
        <div className="space-y-2">
          <div className="flex items-start gap-4">
            <div className="text-[#242424] text-[14px] font-medium min-w-[140px]">Attachment</div>
            <div className="text-[#595959] text-[14px] break-words">
              <img
                src={proposal.file_url}
                alt={proposal.original_name || "attachment"}
                className="max-h-48 max-w-[360px] rounded-md object-contain border border-[#ECECEC] bg-white"
              />
              {/* Filename subtext intentionally removed */}
            </div>
          </div>
        </div>
      );
    }

    // Helper to normalize keys for comparison (lowercase, remove spaces/underscores)
    const normalizeKey = (k) => String(k || "").toLowerCase().replace(/[ _]/g, "");
    const denyKeys = new Set(["id", "createdat"]);

    const entries = Object.entries(proposal)
      // Remove empty values
      .filter(([key, value]) => value !== null && value !== undefined && value !== "")
      // Remove id and created_at/createdAt/created at fields
      .filter(([key]) => !denyKeys.has(normalizeKey(key)));

    if (entries.length === 0) {
      return (
        <p className="text-[#595959] text-[14px]">
          No additional details were provided with this change request.
        </p>
      );
    }

    const humanize = (k) =>
      String(k)
        .replace(/_/g, " ")
        .replace(/\b\w/g, (c) => c.toUpperCase());

    const renderValue = (key, val) => {
      // Single media object { id, file_url, original_name }
      if (
        val &&
        typeof val === "object" &&
        !Array.isArray(val) &&
        typeof val.file_url === "string"
      ) {
        return (
          <div className="flex flex-col gap-1">
            <img
              src={val.file_url}
              alt={val.original_name || "attachment"}
              className="max-h-48 max-w-[360px] rounded-md object-contain border border-[#ECECEC] bg-white"
            />
          </div>
        );
      }

      // Array of media objects
      if (Array.isArray(val)) {
        const mediaItems = val.filter(
          (itm) => itm && typeof itm === "object" && typeof itm.file_url === "string"
        );
        if (mediaItems.length) {
          return (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {mediaItems.map((m, idx) => (
                <div key={idx} className="flex flex-col gap-1">
                  <img
                    src={m.file_url}
                    alt={m.original_name || `attachment-${idx + 1}`}
                    className="h-32 w-full rounded-md object-cover border border-[#ECECEC] bg-white"
                  />
                </div>
              ))}
            </div>
          );
        }
      }

      // Fallback: primitive or generic object
      if (typeof val === "object") return JSON.stringify(val);
      if (shouldCurrencyFormat(key, val)) return formatCurrency(val);
      if (String(key || "").toLowerCase().includes("timeframe")) return formatScheduledTime(val);
      return String(val);
    };

    return (
      <div className="space-y-2">
        {entries.map(([key, value]) => (
          <div key={key} className="flex items-start justify-between gap-4">
            <span className="text-[#242424] text-[14px] font-medium min-w-[140px]">
              {humanize(key)}
            </span>
            <div className="text-[#595959] text-[14px] break-words">
              {renderValue(key, value)}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      maxWidth="756px"
      title="Respond to Change Request"
      footer={
        <div className="flex w-full justify-between">
          <ButtonComponent paddingY="10px" onClick={onClose}>
            Close
          </ButtonComponent>
          <div className="flex gap-2">
            <ButtonComponent
              bgColor="#D40000"
              color="#FFFFFF"
              paddingY="10px"
              onClick={onDeny}
            >
              Deny Request
            </ButtonComponent>
            <ButtonComponent
              bgColor="#3CAB5C"
              color="#FFFFFF"
              paddingY="10px"
              onClick={onAccept}
            >
              Accept Request
            </ButtonComponent>
          </div>
        </div>
      }
    >
      <div className="px-4 py-4">
        <h3 className="text-[16px] font-semibold text-[#242424] mb-2">Change Request Details</h3>
        {renderProposalDetails()}
      </div>
    </Modal>
  );
};

export default ProposalRequestAcceptDeny;
